/**
 * Settings: what is stored, its defaults, and what it resolves to.
 *
 * Stored settings keep "auto" choices as auto: the energy unit follows the home region's
 * labels and the currency follows the home region until the person picks otherwise. Screens
 * read the RESOLVED settings (`useSettings().resolved`), never the raw ones.
 */
import type { PlanTargets, Region, RegionCode } from '@resupport/core'
import { countryOf, normaliseTargets, type EnergyUnit, type MassUnit } from '../engine'

export type ThemeChoice = 'system' | 'light' | 'dark'

export interface SyncConsent {
  /** The person said yes to syncing trips, foods, recipes and ratings. Rule 5: off by default. */
  enabled: boolean
  /** Share availability ratings anonymously to build community figures. Separate yes. */
  share_votes: boolean
  /** When they said yes, for the sheet to show. */
  consented_at?: string
}

/**
 * The person, for the kcal/day suggestion (core `estimateKcalPerDay`). Kilograms, whatever
 * the mass unit shown.
 */
export interface PersonProfile {
  body_kg?: number
  /** Average pack weight over a section: base + food + water. */
  pack_kg?: number
  /** Hours walked on a usual day; a section can say otherwise. */
  hours_per_day?: number
}

export interface Settings {
  /** Home region (where you usually shop). New trips default to it. */
  region?: RegionCode
  /** 'auto' follows the home region's nutrition labels. */
  energy_unit: 'auto' | EnergyUnit
  /** How mass is shown. 'auto' is ounces in the US, grams everywhere else. Display only. */
  mass_unit?: 'auto' | MassUnit
  profile?: PersonProfile
  /** Nutrition targets for the plan; missing fields take core `DEFAULT_TARGETS`. */
  targets?: Partial<PlanTargets>
  /** ISO 4217 to show prices in when a price has none; undefined follows the region. */
  currency?: string
  /** "$12.40" or "AUD 12.40". */
  currency_display: 'symbol' | 'code'
  theme: ThemeChoice
  sync: SyncConsent
  /**
   * The person agreed to send nutrition-panel photos to the server to be read (Shop → quick
   * entry). Asked once per device; absent means not asked yet.
   */
  panel_consent?: boolean
  updated_at?: string
}

export const DEFAULT_SETTINGS: Settings = {
  energy_unit: 'auto',
  currency_display: 'symbol',
  theme: 'system',
  sync: { enabled: false, share_votes: false },
}

export interface ResolvedSettings {
  region?: RegionCode
  /** The Region record for `region`, falling back from subdivision to country. */
  regionInfo?: Region
  energy_unit: EnergyUnit
  mass_unit: MassUnit
  profile: PersonProfile
  /** Complete: stored targets over the defaults. */
  targets: PlanTargets
  currency: string
  currency_display: 'symbol' | 'code'
  theme: ThemeChoice
  sync: SyncConsent
}

/** Countries whose shelves and hiker sheets speak ounces. */
const OUNCE_COUNTRIES = new Set(['US'])

/** "ES-GA" → ES-GA's record if there is one, else ES's. */
export function findRegion(
  regions: ReadonlyMap<RegionCode, Region>,
  code: RegionCode | undefined,
): Region | undefined {
  if (!code) return undefined
  return regions.get(code) ?? regions.get(countryOf(code))
}

/** A first guess at the home region from the browser locale ("en-AU" → "AU"). */
export function regionFromLocale(locale: string | undefined): RegionCode | undefined {
  if (!locale) return undefined
  try {
    const region = new Intl.Locale(locale).maximize().region
    return region && /^[A-Z]{2}$/.test(region) ? region : undefined
  } catch {
    return undefined
  }
}

export function resolveSettings(
  settings: Settings,
  regions: ReadonlyMap<RegionCode, Region>,
  localeRegion?: RegionCode,
): ResolvedSettings {
  const region = settings.region ?? localeRegion
  const regionInfo = findRegion(regions, region)
  const energy_unit: EnergyUnit =
    settings.energy_unit !== 'auto'
      ? settings.energy_unit
      : regionInfo?.label_energy === 'kJ'
        ? 'kJ'
        : 'kcal'
  const mass_unit: MassUnit =
    settings.mass_unit === 'g' || settings.mass_unit === 'oz'
      ? settings.mass_unit
      : region && OUNCE_COUNTRIES.has(countryOf(region))
        ? 'oz'
        : 'g'
  return {
    ...(region ? { region } : {}),
    ...(regionInfo ? { regionInfo } : {}),
    energy_unit,
    mass_unit,
    profile: settings.profile ?? {},
    targets: normaliseTargets(settings.targets),
    currency: settings.currency ?? regionInfo?.currency ?? 'USD',
    currency_display: settings.currency_display,
    theme: settings.theme,
    sync: settings.sync,
  }
}

/** Anything (a stored row from an older version, an import) to valid Settings. */
export function normaliseSettings(raw: unknown): Settings {
  const r = (typeof raw === 'object' && raw !== null ? raw : {}) as Partial<Settings>
  const sync = (typeof r.sync === 'object' && r.sync !== null ? r.sync : {}) as Partial<SyncConsent>
  const out: Settings = {
    energy_unit:
      r.energy_unit === 'kcal' || r.energy_unit === 'kJ'
        ? r.energy_unit
        : DEFAULT_SETTINGS.energy_unit,
    currency_display: r.currency_display === 'code' ? 'code' : 'symbol',
    theme: r.theme === 'light' || r.theme === 'dark' ? r.theme : 'system',
    sync: {
      enabled: sync.enabled === true,
      share_votes: sync.share_votes === true,
      ...(typeof sync.consented_at === 'string' ? { consented_at: sync.consented_at } : {}),
    },
  }
  if (typeof r.region === 'string' && r.region) out.region = r.region
  if (typeof r.currency === 'string' && /^[A-Z]{3}$/.test(r.currency)) out.currency = r.currency
  if (r.mass_unit === 'g' || r.mass_unit === 'oz') out.mass_unit = r.mass_unit
  const profile = normaliseProfile(r.profile)
  if (profile) out.profile = profile
  const targets = normalisePartialTargets(r.targets)
  if (targets) out.targets = targets
  if (typeof r.panel_consent === 'boolean') out.panel_consent = r.panel_consent
  if (typeof r.updated_at === 'string') out.updated_at = r.updated_at
  return out
}

const positive = (v: unknown, max: number): number | undefined =>
  typeof v === 'number' && Number.isFinite(v) && v > 0 && v <= max ? v : undefined

function normaliseProfile(raw: unknown): PersonProfile | undefined {
  if (typeof raw !== 'object' || raw === null) return undefined
  const r = raw as Record<string, unknown>
  const out: PersonProfile = {}
  const body = positive(r.body_kg, 500)
  const pack = positive(r.pack_kg, 200)
  const hours = positive(r.hours_per_day, 24)
  if (body !== undefined) out.body_kg = body
  if (pack !== undefined) out.pack_kg = pack
  if (hours !== undefined) out.hours_per_day = hours
  return Object.keys(out).length ? out : undefined
}

const TARGET_KEYS: (keyof PlanTargets)[] = [
  'protein_share',
  'fat_share',
  'carb_share',
  'sodium_mg_per_1000kcal',
  'kcal_per_g',
  'kcal_tolerance',
  'macro_tolerance',
]

/** Only the fields the person set, each a finite non-negative number. */
function normalisePartialTargets(raw: unknown): Partial<PlanTargets> | undefined {
  if (typeof raw !== 'object' || raw === null) return undefined
  const r = raw as Record<string, unknown>
  const out: Partial<PlanTargets> = {}
  for (const k of TARGET_KEYS) {
    const v = r[k]
    if (typeof v === 'number' && Number.isFinite(v) && v >= 0) out[k] = v
  }
  return Object.keys(out).length ? out : undefined
}
